import * as React from 'react'
import { useParams } from 'react-router-dom'

import { PusherEvent, usePusherEvent } from '~publish/services/pusher'

import {
  clearPostBackfilledComments,
  usePostHasBackfilledComments,
} from '../comments/backfilledPostCommentsSignal'
import { registerIngestedEngagementId } from '../engagement/newEngagementSignal'
import { useIsCommunityRoute } from '../navigation/useIsCommunityRoute'

type NewCommentReceivedHookResponse = {
  showNewCommentsButton: boolean
  hideNewCommentsButton: () => void
}

type UseNewCommentsAvailableButtonProps = {
  channelId?: string
  /**
   * The channels the all-channels view is currently filtered to. A comment from
   * outside this set will never appear in the list, so its id must not be
   * recorded as one the user can catch up with.
   */
  channelIds?: string[]
  postId?: string
}

/**
 * The producer puts the raw comment entity on the wire, so the id arrives as
 * Mongo's `_id`, with no `id` field to read. The value matches the `id` the
 * GraphQL layer returns for the same comment.
 */
type CommentIngestedEventData = {
  channelId?: string
  comment?: { _id?: string; postId?: string }
}

type BackfillDoneEventData = {
  channelId?: string
  newCommentsCount?: number
  message?: string
}

export const useNewCommentsAvailableButton = ({
  channelId: propChannelId,
  channelIds: filterChannelIds,
  postId: filterPostId,
}: UseNewCommentsAvailableButtonProps = {}): NewCommentReceivedHookResponse => {
  const [showNewCommentsButton, setShowNewCommentsButton] =
    React.useState(false)

  /**
   * A backfill names a channel and a count, never a post, so in the by-post
   * view the selected post's own unreplied count is what reports whether the
   * backfill landed on the post the user has open.
   */
  const selectedPostGainedComments = usePostHasBackfilledComments(filterPostId)

  const hideNewCommentsButton = React.useCallback(() => {
    setShowNewCommentsButton(false)
    if (filterPostId) {
      clearPostBackfilledComments(filterPostId)
    }
  }, [filterPostId])

  // Changing which post is selected invalidates whatever the pill was
  // announcing. A pill raised while no post was selected describes a channel
  // and names no post, so it must not survive into the by-post view. And
  // selecting a post loads its comments, so anything a backfill added to it
  // while it was unselected is already on screen. Either way the new scope
  // starts clean rather than announcing comments the user can already read.
  React.useEffect(() => {
    setShowNewCommentsButton(false)
    if (filterPostId) {
      clearPostBackfilledComments(filterPostId)
    }
  }, [filterPostId])

  const isCommentsPage = useIsCommunityRoute()

  // Extract channelId from URL params when on a channel-specific route
  const { channelId: urlChannelId } = useParams<{
    channelId: string
    view?: string
  }>()

  // Use prop channelId if provided, otherwise use URL channelId
  const channelId = propChannelId || urlChannelId

  /**
   * Subscription to COMMENT_INGESTED event
   */
  usePusherEvent(
    PusherEvent.COMMENT_INGESTED,
    (data: CommentIngestedEventData): void => {
      if (isCommentsPage) {
        const channelMatches =
          !channelId || // Show if no specific channel filter
          !data.channelId || // Show if event has no channel info
          channelId === data.channelId // Show if channels match

        // When viewing a specific post, only show button if the comment belongs to that post
        const postMatches =
          !filterPostId || // Show if not filtering by post
          data.comment?.postId === filterPostId // Show if post matches

        if (channelMatches && postMatches) {
          setShowNewCommentsButton(true)

          // Only record ids the list can actually surface. In a filtered
          // all-channels view the event still matches above (there is no single
          // channel to compare against), but a comment from a channel that is
          // filtered out never loads, and an id that never loads would keep the
          // pill up for good.
          const isInFilteredChannels =
            !data.channelId ||
            !filterChannelIds ||
            filterChannelIds.length === 0 ||
            filterChannelIds.includes(data.channelId)

          if (data.comment?._id && isInFilteredChannels) {
            registerIngestedEngagementId('comment', data.comment._id)
          }
        } else {
          setShowNewCommentsButton(false)
        }
      } else {
        setShowNewCommentsButton(false)
      }
    },
  )

  /**
   * Subscription to CHANNEL_BACKFILL_DONE event
   * Show button only when newCommentsCount > 0
   *
   * The event reports a channel and a count with no post attached, so it says
   * nothing about the post the by-post view has open. There it is ignored, and
   * {@link selectedPostGainedComments} announces the backfill instead.
   */
  usePusherEvent(
    PusherEvent.CHANNEL_BACKFILL_DONE,
    (data: BackfillDoneEventData): void => {
      if (
        isCommentsPage &&
        !filterPostId &&
        data.newCommentsCount &&
        data.newCommentsCount > 0
      ) {
        const shouldShowButton =
          !channelId || // Show if no specific channel filter
          !data.channelId || // Show if event has no channel info
          channelId === data.channelId // Show if channels match
        setShowNewCommentsButton(shouldShowButton)
      }
    },
  )

  return {
    showNewCommentsButton: showNewCommentsButton || selectedPostGainedComments,
    hideNewCommentsButton,
  }
}
